import type {
  FindingListEnrichedDto,
  FindingDetailEnrichedDto,
  FindingDetailEnrichedDrilldownDto,
} from '@terra/shared-contracts-findings';
import type {
  InternalEnrichedFinding,
  CustomerEnrichedFinding,
  EnrichedAsset,
  InternalFindingStatus,
} from '../types/enrichedFindings';
import type { EndpointReturnDto } from '@terra/shared-contracts-endpoints';
import { getEndpointApiTypeLabel } from './endpoint-asset-display.utils';

type Asset = FindingListEnrichedDto['assets'][number];

/**
 * Type guard to check if finding is a detail DTO with all fields
 */
function isDetailEnrichedDto(
  finding: FindingListEnrichedDto | FindingDetailEnrichedDto,
): finding is FindingDetailEnrichedDto {
  return Object.prototype.hasOwnProperty.call(finding, 'description');
}

/**
 * Convert enriched assets to UI format
 */
function convertToEnrichedAssets(
  assets: Array<Asset>,
  endpoints: EndpointReturnDto[] = [],
): EnrichedAsset[] {
  return assets.map((asset) => {
    if (asset.type === 'ENDPOINT') {
      const matchingEndpoint = endpoints.find(
        (endpoint) => endpoint.id === asset.id,
      );

      if (matchingEndpoint) {
        return {
          type: asset.type,
          id: asset.id,
          method: matchingEndpoint.request.method || null,
          path: matchingEndpoint.url || null,
          url: matchingEndpoint.url || null,
          graphqlOperationName: matchingEndpoint.graphqlOperationName || null,
          soapOperationName: matchingEndpoint.soapOperationName || null,
          apiProtocol:
            asset.apiProtocol ?? getEndpointApiTypeLabel(matchingEndpoint),
          lifecycle: matchingEndpoint.lifecycle ?? asset.lifecycle,
        };
      }

      return {
        type: asset.type,
        id: asset.id,
        method: asset.method ?? null,
        path: asset.path ?? null,
        url: asset.url ?? null,
        graphqlOperationName: asset.graphqlOperationName ?? null,
        soapOperationName: asset.soapOperationName ?? null,
        apiProtocol: asset.apiProtocol ?? getEndpointApiTypeLabel(asset),
        lifecycle: asset.lifecycle,
      };
    }

    return {
      type: asset.type,
      id: asset.id,
      method: asset.method ?? null,
      path: asset.path ?? null,
      url: asset.url ?? null,
      graphqlOperationName: null,
      soapOperationName: null,
      apiProtocol: null,
      host: asset.host ?? null,
      port: asset.port ?? null,
      discoveredAssetType: asset.discoveredAssetType ?? null,
      lifecycle: asset.lifecycle,
    };
  });
}

/**
 * Convert server-side enriched list finding to UI InternalEnrichedFinding type
 */
export function enrichListFindingWithAssets(
  finding: FindingListEnrichedDto,
  endpoints: EndpointReturnDto[] = [],
): InternalEnrichedFinding {
  const enrichedAssets = convertToEnrichedAssets(finding.assets, endpoints);
  const { signal: signalSummary, ...findingWithoutSignal } = finding;

  return {
    ...findingWithoutSignal,
    status: finding.status as InternalFindingStatus,
    assets: enrichedAssets,
    rawAssets: finding.assets.map((asset) => ({
      type: asset.type,
      id: asset.id,
    })),
    signalSummary: signalSummary ?? null,
    description: null,
    poc: null,
    pocBlocks: null,
    businessImpact: null,
    attackCapabilities: null,
    mitigationPlan: null,
    findingReport: null,
    exploitationGuide: null,
    executionScript: null,
    pocScript: null,
    pocScriptBlockedByGuardrails: null,
    pocScriptGuardrailsFeedback: null,
    remediationPlan: null,
    remediationPlanGeneratedAt: null,
    remediationPr: null,
    isPublished: finding.isPublished ?? null,
    externalReferences: finding.externalReferences ?? [],
  };
}

/**
 * Convert server-side enriched detail finding to UI InternalEnrichedFinding type
 */
export function enrichDetailFindingWithAssets(
  finding: FindingDetailEnrichedDto,
  endpoints: EndpointReturnDto[] = [],
): InternalEnrichedFinding {
  const enrichedAssets = convertToEnrichedAssets(
    finding.assets || [],
    endpoints,
  );

  return {
    ...finding,
    status: finding.status as InternalFindingStatus,
    assets: enrichedAssets,
    rawAssets: (finding.assets ?? []).map((asset) => ({
      type: asset.type,
      id: asset.id,
    })),
  };
}

/**
 * Convert server-side enriched finding to UI InternalEnrichedFinding type.
 * Automatically detects if it's a list or detail DTO and uses the appropriate converter.
 */
export function enrichFindingWithAssets(
  finding:
    | FindingListEnrichedDto
    | FindingDetailEnrichedDto
    | FindingDetailEnrichedDrilldownDto,
  endpoints: EndpointReturnDto[] = [],
): InternalEnrichedFinding {
  if (isDetailEnrichedDto(finding)) {
    return enrichDetailFindingWithAssets(finding, endpoints);
  }

  return enrichListFindingWithAssets(finding, endpoints);
}

export function filterForCustomers(
  findings: InternalEnrichedFinding[],
): CustomerEnrichedFinding[] {
  return findings.map((finding) => finding as CustomerEnrichedFinding);
}
